/**
 * Chiquel "Try It On" - Variant Detector & Button Controller
 * Detects Shopify product variant selection, captures swatch data,
 * and manages the try-on iframe modal experience.
 */
(function () {
  'use strict';

  // The snippet can be rendered more than once (Prestige outputs the product
  // form in both desktop and mobile layouts), which includes this script
  // multiple times. Run the logic only once.
  if (window.__CHIQUEL_TRYON_INIT__) return;
  window.__CHIQUEL_TRYON_INIT__ = true;

  var CHIQUEL_TRYON = window.CHIQUEL_TRYON || {};

  // ---- Configuration (injected from liquid) ----
  var config = {
    appUrl: CHIQUEL_TRYON.appUrl || '',
    eligibilityTag: CHIQUEL_TRYON.eligibilityTag || 'tryon-enabled',
    buttonText: CHIQUEL_TRYON.buttonText || 'Try It On',
    colorOptionName: CHIQUEL_TRYON.colorOptionName || 'Color',
    productTags: CHIQUEL_TRYON.productTags || [],
    productType: CHIQUEL_TRYON.productType || '',
    productId: CHIQUEL_TRYON.productId || '',
    productHandle: CHIQUEL_TRYON.productHandle || '',
    productTitle: CHIQUEL_TRYON.productTitle || '',
    productImages: CHIQUEL_TRYON.productImages || [],
    variants: CHIQUEL_TRYON.variants || [],
    shopDomain: CHIQUEL_TRYON.shopDomain || '',
    currency: CHIQUEL_TRYON.currency || 'USD',
    currentVariantId: CHIQUEL_TRYON.currentVariantId || null,
    forceEligible: CHIQUEL_TRYON.forceEligible || false,
    // ---- Cookie/parameter gate (soft launch) ----
    // When requireGate is true, the button only appears for visitors who have
    // arrived with the URL parameter (which then sets a cookie) or already hold
    // the cookie. e.g. ?tryon=on to enable, ?tryon=off to disable.
    requireGate: CHIQUEL_TRYON.requireGate || false,
    gateParam: CHIQUEL_TRYON.gateParam || 'tryon',
    gateCookie: CHIQUEL_TRYON.gateCookie || 'chiquel_tryon',
    gateValue: CHIQUEL_TRYON.gateValue || '',
    gateDays: CHIQUEL_TRYON.gateDays || 30
  };

  // ---- State ----
  var state = {
    selectedVariant: null,
    iframeReady: false,
    modalOpen: false
  };

  // ---- Eligibility Check ----
  // ---- Cookie / URL-parameter gate ----
  // ?tryon=on  -> sets cookie, button shows (persists for gateDays)
  // ?tryon=off -> clears cookie, button hidden
  // no param   -> shows only if the cookie is already set
  function isGateOpen() {
    if (!config.requireGate) return true;

    var cookieName = config.gateCookie;
    var hasCookie = new RegExp('(?:^|;\\s*)' + cookieName + '=1(?:;|$)')
      .test(document.cookie);

    var params = new URLSearchParams(window.location.search);
    if (params.has(config.gateParam)) {
      var val = (params.get(config.gateParam) || '').toLowerCase();
      var isOff = (val === 'off' || val === '0' || val === 'false' || val === 'no');
      if (isOff) {
        document.cookie = cookieName + '=; path=/; max-age=0; SameSite=Lax';
        return false;
      }
      // Optional shared secret: if gateValue is set, the param must match it.
      if (config.gateValue && val !== String(config.gateValue).toLowerCase()) {
        return hasCookie;
      }
      var maxAge = config.gateDays * 24 * 60 * 60;
      document.cookie = cookieName + '=1; path=/; max-age=' + maxAge + '; SameSite=Lax';
      return true;
    }

    return hasCookie;
  }

  function isProductEligible() {
    // When the snippet is placed only on chosen templates, eligibility is
    // already decided server-side — force it on.
    if (config.forceEligible) return true;
    if (config.productType && config.productType.toLowerCase() === 'wig') {
      return true;
    }
    if (config.eligibilityTag && config.productTags.length > 0) {
      var tag = config.eligibilityTag.toLowerCase();
      for (var i = 0; i < config.productTags.length; i++) {
        if (config.productTags[i].toLowerCase() === tag) return true;
      }
    }
    return false;
  }

  // ---- Swatch Reference Detection ----
  function getSwatchRef(variant) {
    if (!variant) return null;

    // Check metafields if available on the variant
    if (variant.metafields) {
      if (variant.metafields.custom && variant.metafields.custom.color_swatch) {
        return { type: 'swatch_image', value: variant.metafields.custom.color_swatch };
      }
      if (variant.metafields.custom && variant.metafields.custom.color_hex) {
        return { type: 'hex', value: variant.metafields.custom.color_hex };
      }
    }

    // Check option values for the color option
    var colorName = getColorOptionValue(variant);
    if (colorName) {
      // Shopify stores often have swatch files at /files/{handle}_{color}.png
      return { type: 'option_name', value: colorName };
    }

    // Fall back to variant featured image
    if (variant.featured_image && variant.featured_image.src) {
      return { type: 'image', value: variant.featured_image.src };
    }

    return null;
  }

  // ---- Color Option Extraction ----
  function getColorOptionValue(variant) {
    if (!variant || !variant.options) return null;

    // Find which option index corresponds to "Color"
    var product = window.CHIQUEL_TRYON.productJSON;
    if (product && product.options) {
      for (var i = 0; i < product.options.length; i++) {
        var optName = product.options[i].toLowerCase();
        var target = config.colorOptionName.toLowerCase();
        if (optName === target || optName === 'color' || optName === 'colour') {
          return variant.options[i] || null;
        }
      }
    }

    // Fallback: check variant title for color-like values
    if (variant.title) {
      return variant.title.split(' / ')[0] || variant.title;
    }
    return null;
  }

  // ---- Get variant image from DOM swatch elements ----
  function normalizeImageUrl(url) {
    if (!url) return null;
    // Convert protocol-relative URLs to https
    if (url.indexOf('//') === 0) {
      return 'https:' + url;
    }
    return url;
  }

  function getVariantImageFromDOM(variantId) {
    // Prestige/Maestrooo theme stores color images on swatch data attributes
    var swatch = document.querySelector(
      '[data-variant-id="' + variantId + '"][data-color-image-large]'
    );
    if (swatch) {
      return normalizeImageUrl(swatch.getAttribute('data-color-image-large'));
    }

    // Fallback: check for other common swatch patterns
    var swatchAlt = document.querySelector(
      '[data-variant-id="' + variantId + '"] img'
    );
    if (swatchAlt && swatchAlt.src) {
      return normalizeImageUrl(swatchAlt.src);
    }

    return null;
  }

  // ---- Find the on-page swatch image whose alt matches the colour name ----
  // This theme renders per-colour chips as separate images, e.g.
  // <img alt="R1HH BLACK" src=".../r1hh-black.jpg">, with no variant image or
  // colour metafield. Match by alt text so we use the correct colour, not the
  // product's default-colour model photo.
  function getSwatchImageByColorName(name) {
    var target = String(name || '').trim().toLowerCase();
    if (!target) return null;
    var imgs = document.querySelectorAll('img[alt]');
    var fallback = null;
    for (var i = 0; i < imgs.length; i++) {
      var alt = (imgs[i].getAttribute('alt') || '').trim().toLowerCase();
      if (alt !== target) continue;
      var src = imgs[i].currentSrc || imgs[i].src;
      if (!src) continue;
      // Prefer a dedicated swatch chip over a lifestyle/model photo.
      if (/model|lifestyle|front|back|side/i.test(src)) {
        if (!fallback) fallback = src;
        continue;
      }
      return normalizeImageUrl(src);
    }
    return normalizeImageUrl(fallback);
  }

  // ---- Build payload for the try-on app ----
  function buildPayload(variant) {
    var swatchRef = getSwatchRef(variant);
    var colorName = getColorOptionValue(variant);

    // COLOR reference — the variant's own swatch/colour image. NOT the product
    // photo, which is shot in a single default colour.
    var colorImage = null;
    if (variant.featured_image && variant.featured_image.src) {
      colorImage = variant.featured_image.src;            // Priority 1
    }
    if (!colorImage) {
      colorImage = getVariantImageFromDOM(variant.id);    // Priority 2 (data attrs)
    }
    if (!colorImage) {
      colorImage = getSwatchImageByColorName(colorName);  // Priority 3 (alt match)
    }

    // STYLE reference — a model/product photo for length, texture and cut.
    var styleImage = (config.productImages && config.productImages.length)
      ? config.productImages[0] : null;

    return {
      shop_domain: config.shopDomain,
      product_id: config.productId,
      product_handle: config.productHandle,
      product_title: config.productTitle,
      variant_id: variant.id,
      variant_title: variant.title || '',
      selected_color_name: colorName || '',
      variant_image: colorImage,
      style_image: styleImage,
      product_images: config.productImages,
      price: variant.price,
      currency: config.currency,
      swatch_ref: swatchRef
    };
  }

  // ---- Variant Change Detection ----
  function findVariantById(id) {
    for (var i = 0; i < config.variants.length; i++) {
      if (String(config.variants[i].id) === String(id)) {
        return config.variants[i];
      }
    }
    return null;
  }

  function onVariantChange(variantId) {
    var variant = findVariantById(variantId);
    state.selectedVariant = variant;

    var btns = document.querySelectorAll('.chiquel-tryon-btn');
    if (!btns.length) return;

    var enabled = !!(variant && variant.available);
    btns.forEach(function (btn) {
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      var label = btn.querySelector('.chiquel-tryon-btn-text');
      if (label) label.textContent = config.buttonText;
    });
  }

  // Listen for Shopify's native variant change events
  function attachVariantListeners() {
    // Method 1: URL change (most Shopify themes update ?variant= param)
    var lastVariantParam = '';
    function checkUrlVariant() {
      var params = new URLSearchParams(window.location.search);
      var vid = params.get('variant');
      if (vid && vid !== lastVariantParam) {
        lastVariantParam = vid;
        onVariantChange(vid);
      }
    }

    // Poll URL changes (covers pushState and replaceState)
    setInterval(checkUrlVariant, 300);
    checkUrlVariant();

    // Method 2: Listen for Shopify section rendering events
    document.addEventListener('shopify:section:load', function () {
      setTimeout(checkUrlVariant, 100);
    });

    // Method 3: Listen for variant select change/click events on product form
    document.addEventListener('change', function (e) {
      var target = e.target;
      // Variant <select> element
      if (target.name === 'id' && target.tagName === 'SELECT') {
        onVariantChange(target.value);
        return;
      }
      // Variant radio buttons
      if (target.type === 'radio' && target.name && target.name.indexOf('option') > -1) {
        setTimeout(checkUrlVariant, 150);
      }
      // Generic selects for options
      if (target.tagName === 'SELECT' && target.closest('[data-product-form]')) {
        setTimeout(checkUrlVariant, 150);
      }
    });

    // Method 4: Observe hidden input[name="id"] (many themes use this)
    var hiddenInput = document.querySelector('form[action*="/cart/add"] input[name="id"][type="hidden"]');
    if (hiddenInput) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'value') {
            onVariantChange(hiddenInput.value);
          }
        }
      });
      observer.observe(hiddenInput, { attributes: true });
    }

    // Method 5: Swatch button clicks (common in Shopify 2.0 themes)
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest('[data-option-value], .swatch, .color-swatch, .variant-swatch');
      if (swatch) {
        setTimeout(checkUrlVariant, 200);
      }
    });

    // Set initial variant if already selected
    if (config.currentVariantId) {
      onVariantChange(config.currentVariantId);
    }
  }

  // ---- Try It On Button ----
  function createTryOnButton() {
    var btn = document.createElement('button');
    btn.className = 'chiquel-tryon-btn';
    btn.type = 'button';
    btn.disabled = !config.currentVariantId;
    btn.setAttribute('aria-disabled', !config.currentVariantId ? 'true' : 'false');
    btn.setAttribute('aria-label', config.buttonText);

    btn.innerHTML =
      '<span class="chiquel-tryon-btn-text">' + config.buttonText + '</span>';

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      openTryOnModal();
    });

    return btn;
  }

  function injectButton() {
    // Preferred: explicit placement slot(s) rendered by the Liquid snippet.
    // The block can render in more than one place (desktop + mobile product
    // forms, plus stray copies in quick-view/sticky templates). Only use slots
    // that sit inside the real product form so we don't get duplicates.
    var allSlots = [].slice.call(document.querySelectorAll('#chiquel-tryon-slot'));
    var formSlots = allSlots.filter(function (s) {
      return s.closest('form[action*="/cart/add"]');
    });
    var slots = formSlots.length ? formSlots : allSlots.slice(0, 1);
    if (slots.length) {
      slots.forEach(function (slot) {
        if (slot.querySelector('.chiquel-tryon-btn')) return; // idempotent
        var wrap = document.createElement('div');
        wrap.className = 'chiquel-tryon-btn-wrapper';
        wrap.appendChild(createTryOnButton());
        slot.appendChild(wrap);
      });
      return;
    }

    var btn = createTryOnButton();
    var wrapper = document.createElement('div');
    wrapper.className = 'chiquel-tryon-btn-wrapper';
    wrapper.appendChild(btn);

    // Place button right after the color swatch / variant selector area.
    // Try selectors from most specific to least specific.
    var targets = [
      // Chiquel / Prestige / Maestrooo theme — insert after the variants block
      { sel: '.ProductForm__Variants', mode: 'after' },
      // Dawn / Shopify 2.0 themes
      { sel: 'variant-selects', mode: 'after' },
      { sel: 'variant-radios', mode: 'after' },
      // Other common swatch containers
      { sel: '.ColorSwatchList', mode: 'after-parent' },
      { sel: '.product-form__input--swatch', mode: 'after' },
      { sel: '.swatch-container', mode: 'after' },
    ];

    for (var i = 0; i < targets.length; i++) {
      var el = document.querySelector(targets[i].sel);
      if (el) {
        if (targets[i].mode === 'after') {
          // Insert as next sibling of the element
          el.parentElement.insertBefore(wrapper, el.nextSibling);
        } else if (targets[i].mode === 'after-parent') {
          // Insert after the element's parent container
          el.parentElement.parentElement.insertBefore(wrapper, el.parentElement.nextSibling);
        }
        return;
      }
    }

    // Fallback: place after Add to Cart button
    var cartBtn = document.querySelector(
      'form[action*="/cart/add"] button[type="submit"], .product-form__submit, [data-add-to-cart]'
    );
    if (cartBtn) {
      var parent = cartBtn.closest('.ProductForm__AddToCart') ||
                   cartBtn.closest('.product-form__buttons') ||
                   cartBtn.parentElement;
      parent.parentElement.insertBefore(wrapper, parent.nextSibling);
    }
  }

  // ---- Modal / Iframe ----
  function createModal() {
    var overlay = document.createElement('div');
    overlay.id = 'chiquel-tryon-modal-overlay';
    overlay.className = 'chiquel-tryon-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Virtual Try On');

    overlay.innerHTML =
      '<div class="chiquel-tryon-modal">' +
        '<div class="chiquel-tryon-modal-header">' +
          '<span class="chiquel-tryon-modal-title">Try It On</span>' +
          '<button class="chiquel-tryon-modal-close" aria-label="Close try-on">' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
              '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
            '</svg>' +
          '</button>' +
        '</div>' +
        '<div class="chiquel-tryon-modal-body">' +
          '<div class="chiquel-tryon-loading">' +
            '<div class="chiquel-tryon-spinner"></div>' +
            '<p>Loading...</p>' +
          '</div>' +
          '<iframe id="chiquel-tryon-iframe" class="chiquel-tryon-iframe" allow="camera; microphone" loading="lazy" title="Virtual Wig Try On"></iframe>' +
        '</div>' +
      '</div>';

    // Close on overlay click
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        closeTryOnModal();
      }
    });

    // Close button
    overlay.querySelector('.chiquel-tryon-modal-close').addEventListener('click', function () {
      closeTryOnModal();
    });

    document.body.appendChild(overlay);
    return overlay;
  }

  function openTryOnModal() {
    if (!state.selectedVariant) return;

    var overlay = document.getElementById('chiquel-tryon-modal-overlay') || createModal();
    var iframe = document.getElementById('chiquel-tryon-iframe');

    // Build iframe URL
    var payload = buildPayload(state.selectedVariant);
    var params = new URLSearchParams({
      embedded: 'true',
      mode: 'tryon',
      product_id: payload.product_id,
      variant_id: String(payload.variant_id),
      color: payload.selected_color_name || '',
      shop: payload.shop_domain
    });

    // COLOR reference (variant swatch) and STYLE reference (model photo) are
    // sent separately: the backend uses wig_image for colour and product_image
    // for length/texture/style. Never send the product photo as the colour
    // reference — it is shot in one default colour.
    if (payload.variant_image) {
      params.set('wig_image', payload.variant_image);
    }
    if (payload.style_image) {
      params.set('product_image', payload.style_image);
    }

    // Cache-bust to ensure fresh content after deploys
    params.set('_v', Date.now().toString(36));
    var iframeSrc = config.appUrl + '/storefront/try-on?' + params.toString();

    // Only reload iframe if URL changed (ignoring cache-bust param)
    var currentBase = iframe.src.replace(/[&?]_v=[^&]*/, '');
    var newBase = iframeSrc.replace(/[&?]_v=[^&]*/, '');
    if (currentBase !== newBase || !state.iframeReady) {
      iframe.src = iframeSrc;
      state.iframeReady = false;
      overlay.querySelector('.chiquel-tryon-loading').style.display = 'flex';
      iframe.style.opacity = '0';
    }

    overlay.classList.add('chiquel-tryon-overlay--visible');
    document.body.style.overflow = 'hidden';
    state.modalOpen = true;

    // Send payload to iframe once it's loaded
    iframe.onload = function () {
      state.iframeReady = true;
      overlay.querySelector('.chiquel-tryon-loading').style.display = 'none';
      iframe.style.opacity = '1';
      sendPayloadToIframe(iframe, payload);
    };

    // ESC key to close
    document.addEventListener('keydown', handleEscKey);
  }

  function closeTryOnModal() {
    var overlay = document.getElementById('chiquel-tryon-modal-overlay');
    if (!overlay) return;

    overlay.classList.remove('chiquel-tryon-overlay--visible');
    document.body.style.overflow = '';
    state.modalOpen = false;
    document.removeEventListener('keydown', handleEscKey);
  }

  function handleEscKey(e) {
    if (e.key === 'Escape') {
      closeTryOnModal();
    }
  }

  function sendPayloadToIframe(iframe, payload) {
    if (!iframe || !iframe.contentWindow) return;
    try {
      var origin = new URL(config.appUrl).origin;
      iframe.contentWindow.postMessage(
        { type: 'chiquel-tryon-product', payload: payload },
        origin
      );
    } catch (err) {
      // Origin parsing failed; skip
    }
  }

  // ---- Receive Messages from Iframe ----
  function handleIframeMessages(event) {
    if (!config.appUrl) return;
    try {
      var origin = new URL(config.appUrl).origin;
      if (event.origin !== origin) return;
    } catch (err) {
      return;
    }

    var data = event.data;
    if (!data || !data.type) return;

    switch (data.type) {
      case 'chiquel-tryon-close':
        closeTryOnModal();
        break;

      case 'chiquel-tryon-resize':
        var iframe = document.getElementById('chiquel-tryon-iframe');
        if (iframe && data.height) {
          iframe.style.height = Math.max(data.height, 400) + 'px';
        }
        break;

      case 'chiquel-tryon-result-ready':
        // Result is ready - could trigger a notification or update UI
        document.querySelectorAll('.chiquel-tryon-btn').forEach(function (btn) {
          btn.classList.add('chiquel-tryon-btn--has-result');
        });
        break;

      case 'chiquel-tryon-add-to-cart':
        // Add the variant to cart from the try-on experience
        if (data.variantId) {
          addToCart(data.variantId, data.quantity || 1);
        }
        break;
    }
  }

  // ---- Add to Cart Helper ----
  function addToCart(variantId, quantity) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        // Trigger Shopify's cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Some themes listen for this
        if (window.Shopify && window.Shopify.onCartUpdate) {
          window.Shopify.onCartUpdate();
        }
      })
      .catch(function (err) {
        console.error('[Chiquel TryOn] Add to cart failed:', err);
      });
  }

  // ---- Init ----
  function init() {
    if (!isGateOpen()) return;
    if (!isProductEligible()) return;
    if (!config.appUrl) return;

    injectButton();
    attachVariantListeners();
    window.addEventListener('message', handleIframeMessages);
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
